/**
 * №44 — THE APP SHELL'S COMMAND SET, as PURE FUNCTIONS.
 *
 * Data in, `Command[]` out. No hooks, no React, no DOM, no router — the hook
 * layer that feeds these lives next door in `use-app-commands.ts`, and the
 * palette that renders them in `AppCommandPalette.tsx`.
 *
 * ## Why pure, and why that is the point
 *
 * The console's seven command sources are hooks, so nothing can enumerate them
 * outside a browser: the only possible gate is a journey that types into a box.
 * Building the App Shell's set as functions over plain data means
 * `scripts/test-app-palette-44.ts` can enumerate EVERY command and check its
 * destination against the route→anchor matrix. That is what makes the dead-nav
 * ban hold here **by construction** rather than by review — the same move
 * `openRecordNav` made for the nav's sub-rows.
 *
 * ## Where the destinations come from — declared, never listed
 *
 *   Places          `get_app_sitemap` (the list `ShellNav` renders)
 *   Static surfaces the MATRIX's own param-free rows
 *   Sub-views       `openRecordNav(pathname)` — the open record's declared rows
 *   Records         a declared entity type's rows → `/app/place/:entity/:id`
 *   Hires           the roster → `/app/staff/:agentId`
 *
 * A surface the shell does not declare cannot appear here, and one it does
 * declare cannot be forgotten. **No console routes**: `/agents/:id`,
 * `/library/:id`, `/workforce`, `/hire`, `/connections`, `/settings/members`
 * are all console destinations and none of them exists under `/app` — offering
 * one from this palette would be the dead-nav ban with a keyboard.
 *
 * Command ids are prefixed `app:` so they can never collide with the console
 * palette's in the shared per-workspace recency store.
 */
import { ROUTE_ANCHOR_MATRIX, openRecordNav, placeLabel } from "./route-anchor-matrix";
import type { Command } from "../../components/command-palette/types";
import type { SitemapEntry } from "./sitemap";

/** The roster row shape both `ShellNav` and this module read. */
export interface AppRosterMember {
  id: string;
  name: string;
}

/** One record, already reduced to what a command needs. */
export interface AppRecordRow {
  entity: string;
  /** The entity type's human name — "Matter", "Contact". Subtitles read better. */
  entityLabel: string;
  id: string;
  label: string;
}

/** Everything the app's command set is built from. All optional-empty, so a
 *  half-loaded workspace produces a smaller palette rather than a broken one. */
export interface AppCommandData {
  /** The current pathname — only the sub-view commands are context-aware. */
  pathname: string;
  places: SitemapEntry[];
  roster: AppRosterMember[];
  records: AppRecordRow[];
  /** Resolved workbench routes, keyed by workbench name (the registry is a
   *  client-side lookup and stays in the hook layer). */
  workbenchRoutes: Record<string, string | undefined>;
}

/** A shell action — a command that does something rather than going somewhere. */
export interface AppActionSpec {
  id: string;
  label: string;
  icon?: string;
  keywords?: string[];
  subtitle?: string;
  run: () => void;
}

/**
 * Matrix rows that are navigable with no parameters at all — Trust, Registers,
 * Invoices, the WIP worksheet, Firm settings, and so on.
 *
 * Derived from the matrix rather than typed out, so a new param-free surface is
 * offered the day its row lands. `*` (the fallback) and `/app/new` are excluded
 * deliberately: the fallback is not a surface, and `/app/new` is the intake route
 * whose `New…` nav row was REMOVED in №22.6 because its draft-record payoff was
 * never built (`ShellNav.tsx:222–234`). A row David ruled out of the nav does not
 * get to come back in through the palette.
 */
export const PALETTE_EXCLUDED_PATTERNS: readonly string[] = ["*", "/app/new"];

export function staticSurfaceCommands(navigate: (to: string) => void): Command[] {
  const out: Command[] = [];
  for (const row of ROUTE_ANCHOR_MATRIX) {
    if (PALETTE_EXCLUDED_PATTERNS.includes(row.pattern)) continue;
    if (row.pattern.includes(":")) continue;
    out.push({
      id: `app:surface:${row.key}`,
      label: surfaceLabel(row.key, row.surface),
      icon: "arrow-right",
      kind: "nav",
      subtitle: row.pattern,
      keywords: ["go to", row.key, row.surface],
      execute: () => navigate(row.pattern),
    });
  }
  return out;
}

/**
 * A matrix row's `surface` string is the committed table's name, written for a
 * reviewer ("The trust ledger list, all matters") — the same reason `navLabel`
 * exists for the sub-nav. A palette row is read by a user mid-keystroke, so the
 * few surfaces whose table name is a sentence get a short one here; anything
 * else falls through to its own name.
 */
const SURFACE_LABEL: Record<string, string> = {
  "needs-you": "Needs you",
  clients: "Clients",
  registers: "Registers",
  invoices: "Invoices",
  "vendor-invoices": "Vendor invoices",
  "time-day": "Time & billing",
  "wip-worksheet": "WIP billing worksheet",
  "firm-settings": "Firm settings",
  "trust-list": "Trust",
};

function surfaceLabel(key: string, surface: string): string {
  return SURFACE_LABEL[key] ?? surface;
}

/** The Places axis — the same entries the nav renders, same targets. */
export function placeCommands(
  places: SitemapEntry[],
  workbenchRoutes: Record<string, string | undefined>,
  navigate: (to: string) => void,
): Command[] {
  const out: Command[] = [];
  for (const e of places) {
    const target = e.route ?? (e.workbench ? workbenchRoutes[e.workbench] : undefined);
    // A Place with no destination renders nothing in the nav (§3 ⑥, the
    // dead-nav ban) and offers nothing here, for the same reason.
    if (!target) continue;
    out.push({
      id: `app:place:${e.label}`,
      label: e.label,
      icon: "layout-grid",
      kind: "nav",
      subtitle: "Place",
      keywords: ["place", "go to", e.module ?? ""].filter(Boolean),
      execute: () => navigate(target),
    });
  }
  return out;
}

/**
 * The sub-views of the record you already have open — "Parties", "Billing",
 * "Trust", … — straight from `openRecordNav`, which is the same generator the
 * nav's OPEN section uses. Empty on every surface that is not a record, and
 * then the palette simply does not offer them.
 */
export function openRecordCommands(pathname: string, navigate: (to: string) => void): Command[] {
  const open = openRecordNav(pathname);
  if (!open) return [];
  return open.items.map((it) => ({
    id: `app:open:${it.key}`,
    label: `${open.id} · ${it.label}`,
    icon: "corner-down-right",
    kind: "nav" as const,
    subtitle: `Open record · ${placeLabel(open.entity)}`,
    keywords: [it.label, open.id, open.entity, "open", "current"],
    contextAware: true,
    execute: () => navigate(it.path),
  }));
}

/**
 * The entity types the shell actually SURFACES — the slugs behind a Place route.
 *
 * This is the filter the first render proved was missing
 * (`docs/44-command-palette-evidence/palette-search-dark.png`): typing one letter
 * returned `Option · OPT-0030`, `Option set · OPTS-0001` and
 * `Time entry · TIME-0002` alongside the matters and contacts. Those are real
 * rows of real declared types, and `/app/place/option/OPT-0030` even resolves —
 * but an option-set option is not a thing anyone navigates TO, and a palette that
 * offers it has stopped being a way to reach your work.
 *
 * `hide_from_synthesis` does not draw this line (none of those three is hidden;
 * they are legitimately synthesised, addressable types). The line the shell
 * itself draws is the **Places axis**: a type with a Place is a type this product
 * gives you a way to open. Same declared source as everything else here — a
 * module that ships a new Place gets its records in the palette for free, and one
 * that does not, does not.
 */
export function placeBackedEntities(places: SitemapEntry[]): Set<string> {
  const out = new Set<string>();
  for (const e of places) {
    const slug = e.view?.entity_type ?? e.route?.match(/^\/app\/place\/([^/]+)$/)?.[1];
    if (slug) out.add(slug);
  }
  return out;
}

/** Every record of a PLACE-BACKED type, going to the App Shell's record route. */
export function recordCommands(records: AppRecordRow[], navigate: (to: string) => void): Command[] {
  return records.map((r) => ({
    id: `app:record:${r.entity}:${r.id}`,
    label: r.label,
    icon: "list-details",
    kind: "nav" as const,
    subtitle: `${r.entityLabel} · ${r.id}`,
    keywords: [r.entityLabel, r.entity, r.id],
    execute: () => navigate(`/app/place/${r.entity}/${r.id}`),
  }));
}

/**
 * The crew. D-L6 vocabulary — these are staff, and the subtitle says so.
 *
 * A hire's desk is also where their routines live, which is why this spec does
 * NOT offer a "Run routine…" command: `/app` has no routine route, and a command
 * that lands nowhere is the thing the ban exists to prevent.
 */
export function hireCommands(
  roster: AppRosterMember[],
  collective: string,
  navigate: (to: string) => void,
): Command[] {
  return roster.map((m) => ({
    id: `app:hire:${m.id}`,
    label: m.name,
    icon: "user-circle",
    kind: "nav" as const,
    subtitle: collective,
    keywords: ["hire", "staff", "crew", "desk", "routines"],
    execute: () => navigate(`/app/staff/${m.id}`),
  }));
}

/** Shell actions — things that happen here rather than somewhere else. */
export function actionCommands(actions: AppActionSpec[]): Command[] {
  return actions.map((a) => ({
    id: a.id,
    label: a.label,
    icon: a.icon ?? "bolt",
    kind: "action" as const,
    subtitle: a.subtitle,
    keywords: a.keywords,
    execute: a.run,
  }));
}

/**
 * The whole set, in the order the sources are composed. Ranking decides what a
 * user actually sees (`rank.ts`); this order only decides ties.
 */
export function buildAppCommands(
  data: AppCommandData,
  opts: { collective: string; navigate: (to: string) => void; actions: AppActionSpec[] },
): Command[] {
  const { navigate } = opts;
  // The Places filter is applied HERE rather than in the hook, so the gate
  // exercises the same rule the palette does.
  const surfaced = placeBackedEntities(data.places);
  const records = data.records.filter((r) => surfaced.has(r.entity));
  return [
    ...openRecordCommands(data.pathname, navigate),
    ...placeCommands(data.places, data.workbenchRoutes, navigate),
    ...staticSurfaceCommands(navigate),
    ...recordCommands(records, navigate),
    ...hireCommands(data.roster, opts.collective, navigate),
    ...actionCommands(opts.actions),
  ];
}

/**
 * Every DESTINATION this command set navigates to, for the gate.
 *
 * Built by running the real builders with a `navigate` that records instead of
 * routing — so the gate checks the paths the palette would actually push, not a
 * re-derivation of them that could drift from the builders it is meant to prove.
 */
export function appCommandTargets(data: AppCommandData, collective = "Workforce"): string[] {
  const targets: string[] = [];
  const navigate = (to: string) => { targets.push(to); };
  const cmds = buildAppCommands(data, { collective, navigate, actions: [] });
  for (const c of cmds) c.execute();
  return targets;
}

/* ---------------------------------------------------------------------------
 * №53 (53.0, D-CR22) — THE RESTING STATE.
 *
 * ⌘K opens to a POPULATED list, grouped, before a single keystroke. This
 * supersedes №44's deliberate "no browse-everything list behind an empty query".
 *
 * №44's choice was RIGHT WHEN IT WAS MADE. The rail still carried every record
 * list, so the palette was a shortcut for people who already knew where things
 * were, and an invented browse list would have been noise beside a nav that
 * already showed everything.
 *
 * D-CR12 removed those rows. When the palette is the ONLY route to every record
 * list, an empty prompt is not honesty — it is a dead end. David, 2026-08-19:
 * *"It's just not populated or look like its setup."*
 *
 * The groups answer "what can I even do from here?", in the order the mockup
 * draws them (`docs/53-mockups/command-palette.html`, frame 2):
 *
 *   Go to    the day surfaces — the same six the rail shows
 *   Records  every list that used to BE a rail row
 *   Recent   what you actually touched (your history, not the workspace's)
 *   Do       create + firm — the things that act rather than navigate
 *
 * NOTHING HERE IS HAND-KEPT. The groups are a partition of the SAME command set
 * the search ranks, derived from id prefix and the server's `section` mark, so a
 * module that ships a new Place gets its list — and its records — in here for
 * free, and a surface the shell does not declare cannot appear.
 * ------------------------------------------------------------------------- */

export interface RestingGroup {
  key: string;
  heading: string;
  commands: Command[];
}

/** Per-group cap. A resting list is a starting point, not an inventory: past
 *  about eight rows a group stops being scannable and the search is the better
 *  tool — which is two keystrokes away and is what the input says to do. */
const RESTING_CAP = 8;

export function restingGroups(
  all: Command[],
  places: SitemapEntry[],
  recents: Command[],
): RestingGroup[] {
  // The day Places, by the label their command id is built from. The palette
  // does not re-decide which rows are the day's — it reads the same `section`
  // mark the rail reads, so the two can never disagree about the six.
  const dayLabels = new Set(places.filter((p) => p.section === "day").map((p) => p.label));
  const isDayPlace = (c: Command) =>
    c.id.startsWith("app:place:") && dayLabels.has(c.id.slice("app:place:".length));

  const recentIds = new Set(recents.map((c) => c.id));

  // A settings surface is not a record list. `/app/settings/*` rows are generated
  // from the same matrix as every other param-free surface, so without this they
  // would file under `Records` and put "Endpoints" beside "Contacts" — which is
  // how a grouped list stops being worth grouping.
  const isSettings = (c: Command) => c.id.startsWith("app:surface:settings");

  // ── HERE, and it LEADS ──────────────────────────────────────────────────────
  //
  // On a record, that record's own declared sub-views come first. They are
  // `contextAware` commands from `openRecordCommands`, generated by `openRecordNav`
  // — the same generator the record's tab strip uses, so the palette and the tabs
  // cannot disagree about what a record is made of (№44 §7 3, gated).
  //
  // ⚠ I BROKE THIS AND THE GATE CAUGHT IT. The first cut of `restingGroups` had four
  // groups — Go to / Records / Recent / Do — none of which included a `contextAware`
  // command, so a record's sub-views disappeared from ⌘K altogether. My own note
  // claimed "the Here group folds into Go to"; it did not, and nothing folded it
  // anywhere. `app-palette-44` failed on the exact assertion that exists to stop it.
  //
  // №44 capped this group at 6 and put it BELOW recents, because a matter declares
  // ten sub-views and they were also permanently on screen in the rail's OPEN
  // section. №48 deleted that section and D-CR12 emptied the rail, so nothing else
  // on screen says what this record is made of — it leads again, and keeps the cap.
  const here = all.filter((c) => c.contextAware);

  const goTo = all.filter(isDayPlace);
  const records = all.filter(
    (c) => (c.id.startsWith("app:place:") && !isDayPlace(c))
        || (c.id.startsWith("app:surface:") && !isSettings(c)),
  );
  // `Do` is the ACTION commands — the ones that change something rather than go
  // somewhere. They are the group D-CR22 adds; the other three existed as
  // concepts already and simply had nothing rendering them at rest.
  const doing = [...all.filter((c) => c.kind === "action"), ...all.filter(isSettings)];

  const groups: RestingGroup[] = [
    { key: "here", heading: "This record", commands: here },
    { key: "go", heading: "Go to", commands: goTo },
    { key: "records", heading: "Records", commands: records },
    // Recents are already ranked by the recency store; they are not re-sorted
    // here, and anything already shown above is dropped rather than repeated.
    { key: "recent", heading: "Recent", commands: recents.filter((c) => !isDayPlace(c)) },
    { key: "do", heading: "Do", commands: doing.filter((c) => !recentIds.has(c.id)) },
  ];

  // A group with nothing in it does not render a heading. This is the dead-nav
  // ban applied to a list: an empty "Records" heading on a workspace with no
  // modules installed would advertise a section that does not exist.
  return groups
    .map((g) => ({ ...g, commands: g.commands.slice(0, RESTING_CAP) }))
    .filter((g) => g.commands.length > 0);
}
